<template>
  <div>
    <section class="relative overflow-hidden min-h-screen flex items-center pt-16">
      <div class="absolute inset-0 bg-white dark:bg-dark transition-colors duration-300">
        <img v-if="isDark" src="/website_bg.webp" alt="" class="absolute inset-0 w-full h-full object-cover" />
        <img v-else src="/website_bg_light.webp" alt="" class="absolute inset-0 w-full h-full object-cover opacity-40" />
      </div>

      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-5xl mx-auto text-center">
          <p class="text-[13px] uppercase tracking-[0.2em] text-blue-500 font-medium mb-6">
            {{ $t('websiteSolution.specialOffer') }}
          </p>
          <h1 class="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-6 text-gray-900 dark:text-white transition-colors duration-300">
            {{ $t('websiteTicino.hero.title') }}
          </h1>
          <p class="text-lg md:text-xl text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed mb-10 transition-colors duration-300">
            {{ $t('websiteTicino.hero.description') }}
          </p>

          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10">
            <router-link to="/contact" class="inline-flex items-center h-14 px-8 bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl font-bold text-lg transition-colors duration-300">
              <Globe class="mr-2 h-5 w-5" />
              {{ $t('websiteTicino.hero.cta') }}
            </router-link>
            <router-link to="/website" class="inline-flex items-center h-14 px-8 border border-gray-200 dark:border-white/[0.08] text-gray-500 dark:text-gray-400 rounded-xl font-bold text-lg transition-colors duration-300">
              <Monitor class="mr-2 h-5 w-5" />
              {{ $t('websiteTicino.hero.learnMore') }}
            </router-link>
          </div>

          <div class="flex flex-wrap justify-center gap-6 text-sm text-gray-500 dark:text-gray-400">
            <div class="flex items-center"><Check class="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />{{ $t('websiteSolution.trust.freeChatbot') }}</div>
            <div class="flex items-center"><Check class="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />{{ $t('websiteSolution.trust.seo') }}</div>
            <div class="flex items-center"><Check class="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />{{ $t('websiteSolution.trust.support') }}</div>
          </div>
        </div>
      </div>
      <div class="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white dark:from-dark to-transparent z-10"></div>
    </section>

    <PartnersSection />

    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">Ticino</p>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('websiteTicino.ticinoSpecific.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t('websiteTicino.ticinoSpecific.subtitle') }}
          </p>
        </div>

        <div class="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="group rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 transition-all duration-300">
            <div class="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06]">
              <Globe class="h-6 w-6 text-blue-500" />
            </div>
            <h3 class="text-xl font-bold mb-2 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('websiteTicino.ticinoSpecific.local.title') }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ $t('websiteTicino.ticinoSpecific.local.description') }}</p>
          </div>

          <div class="group rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 transition-all duration-300">
            <div class="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06]">
              <Users class="h-6 w-6 text-blue-500" />
            </div>
            <h3 class="text-xl font-bold mb-2 text-gray-900 dark:text-white transition-colors duration-300">{{ $t('websiteTicino.ticinoSpecific.community.title') }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ $t('websiteTicino.ticinoSpecific.community.description') }}</p>
          </div>
        </div>

        <div class="mt-10 text-center">
          <p class="text-gray-500 dark:text-gray-400 mb-4 font-semibold transition-colors duration-300">{{ $t('websiteTicino.ticinoSpecific.learnMore') }}</p>
          <router-link to="/website" class="inline-flex items-center h-14 px-8 bg-gray-900 dark:bg-white text-white dark:text-dark rounded-xl font-bold text-lg transition-colors duration-300">
            <Monitor class="mr-2 h-5 w-5" />
            {{ $t('websiteTicino.ticinoSpecific.exploreGeneral') }}
          </router-link>
        </div>
      </div>
    </section>

    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('websiteTicino.localExpertise.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed transition-colors duration-300">
            {{ $t('websiteTicino.localExpertise.subtitle') }}
          </p>
        </div>

        <div class="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="rounded-2xl p-6 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
            <h4 class="font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('websiteTicino.localExpertise.multilingual.title') }}</h4>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ $t('websiteTicino.localExpertise.multilingual.description') }}</p>
          </div>
          <div class="rounded-2xl p-6 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
            <h4 class="font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('websiteTicino.localExpertise.compliance.title') }}</h4>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ $t('websiteTicino.localExpertise.compliance.description') }}</p>
          </div>
          <div class="rounded-2xl p-6 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
            <h4 class="font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('websiteTicino.localExpertise.support.title') }}</h4>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ $t('websiteTicino.localExpertise.support.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('websiteTicino.whatIsWebsite.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto transition-colors duration-300">
            {{ $t('websiteTicino.whatIsWebsite.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          <div>
            <div class="flex items-center gap-2 mb-8">
              <div class="h-px flex-1 bg-gray-200 dark:bg-white/[0.08]"></div>
              <span class="text-[13px] uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400 font-medium">{{ $t('websiteTicino.whatIsWebsite.problems.title') }}</span>
              <div class="h-px flex-1 bg-gray-200 dark:bg-white/[0.08]"></div>
            </div>
            <div class="space-y-4">
              <div class="flex items-start gap-3"><span class="text-red-400 text-xl font-bold flex-shrink-0">×</span><div><p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('websiteTicino.whatIsWebsite.problems.outdated.title') }}</p><p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('websiteTicino.whatIsWebsite.problems.outdated.description') }}</p></div></div>
              <div class="flex items-start gap-3"><span class="text-red-400 text-xl font-bold flex-shrink-0">×</span><div><p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('websiteTicino.whatIsWebsite.problems.mobile.title') }}</p><p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('websiteTicino.whatIsWebsite.problems.mobile.description') }}</p></div></div>
              <div class="flex items-start gap-3"><span class="text-red-400 text-xl font-bold flex-shrink-0">×</span><div><p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('websiteTicino.whatIsWebsite.problems.seo.title') }}</p><p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('websiteTicino.whatIsWebsite.problems.seo.description') }}</p></div></div>
            </div>
          </div>

          <div>
            <div class="flex items-center gap-2 mb-8">
              <div class="h-px flex-1 bg-blue-200 dark:bg-blue-500/20"></div>
              <span class="text-[13px] uppercase tracking-[0.15em] text-blue-500 font-medium">{{ $t('websiteTicino.whatIsWebsite.solutions.title') }}</span>
              <div class="h-px flex-1 bg-blue-200 dark:bg-blue-500/20"></div>
            </div>
            <div class="space-y-4">
              <div class="flex items-start gap-3"><Check class="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" /><div><p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('websiteTicino.whatIsWebsite.solutions.modern.title') }}</p><p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('websiteTicino.whatIsWebsite.solutions.modern.description') }}</p></div></div>
              <div class="flex items-start gap-3"><Check class="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" /><div><p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('websiteTicino.whatIsWebsite.solutions.responsive.title') }}</p><p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('websiteTicino.whatIsWebsite.solutions.responsive.description') }}</p></div></div>
              <div class="flex items-start gap-3"><Check class="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" /><div><p class="font-medium text-gray-900 dark:text-white transition-colors duration-300">{{ $t('websiteTicino.whatIsWebsite.solutions.optimized.title') }}</p><p class="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{{ $t('websiteTicino.whatIsWebsite.solutions.optimized.description') }}</p></div></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('websiteTicino.benefits.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto transition-colors duration-300">
            {{ $t('websiteTicino.benefits.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="(benefit, index) in benefitsList" :key="index" class="group rounded-2xl p-6 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 transition-all duration-300">
            <div class="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06]">
              <component :is="benefit.icon" class="h-6 w-6 text-blue-500" />
            </div>
            <h3 class="text-xl font-semibold mb-3 text-gray-900 dark:text-white transition-colors duration-300">{{ benefit.title }}</h3>
            <p class="text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ benefit.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('websiteTicino.useCases.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto transition-colors duration-300">
            {{ $t('websiteTicino.useCases.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          <div v-for="useCase in useCases" :key="useCase.key" class="group rounded-2xl p-6 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] hover:border-blue-200 dark:hover:border-blue-500/20 transition-all duration-300">
            <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-3 transition-colors duration-300">{{ useCase.title }}</h3>
            <p class="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed transition-colors duration-300">{{ useCase.description }}</p>
            <ul class="space-y-2 text-sm text-gray-500 dark:text-gray-400 mb-4">
              <li v-for="feature in useCase.features" :key="feature" class="flex items-center">
                <Check class="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                {{ feature }}
              </li>
            </ul>
            <blockquote class="rounded-xl p-4 bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
              <p class="text-sm text-gray-500 dark:text-gray-400 italic">"{{ useCase.testimonialText }}"</p>
              <footer class="text-xs text-gray-400 dark:text-gray-500 mt-1">- {{ useCase.testimonialAuthor }}</footer>
            </blockquote>
          </div>
        </div>
      </div>
    </section>

    <section class="py-28 bg-gray-50 dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('websiteTicino.process.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto transition-colors duration-300">
            {{ $t('websiteTicino.process.subtitle') }}
          </p>
        </div>

        <div class="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div v-for="(step, index) in processSteps" :key="index" class="rounded-2xl p-6 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-center transition-colors duration-300">
            <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] flex items-center justify-center text-sm font-bold text-gray-900 dark:text-white mx-auto mb-4 transition-colors duration-300">
              {{ index + 1 }}
            </div>
            <h3 class="text-lg font-bold mb-2 text-gray-900 dark:text-white transition-colors duration-300">{{ step.title }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ step.description }}</p>
          </div>
        </div>

        <div class="mt-8 max-w-3xl mx-auto rounded-2xl p-6 bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] transition-colors duration-300">
          <h4 class="text-xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">{{ $t('websiteTicino.process.speed.title') }}</h4>
          <p class="text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ $t('websiteTicino.process.speed.description') }}</p>
        </div>
      </div>
    </section>

    <section class="py-28 bg-white dark:bg-dark transition-colors duration-300">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6 transition-colors duration-300">
            {{ $t('websiteTicino.faq.title') }}
          </h2>
          <p class="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto transition-colors duration-300">
            {{ $t('websiteTicino.faq.subtitle') }}
          </p>
        </div>

        <div class="max-w-3xl mx-auto">
          <div class="divide-y divide-gray-200 dark:divide-white/[0.06]">
            <div v-for="(faq, index) in faqQuestions" :key="index">
              <button @click="toggleFaq(index)" class="group w-full flex justify-between items-center py-5 text-left">
                <h3 class="text-[15px] font-semibold text-gray-900 dark:text-white pr-8 transition-colors duration-300 group-hover:text-blue-500">{{ faq.question }}</h3>
                <span class="h-6 w-6 flex items-center justify-center text-gray-400 dark:text-gray-500 transition-all duration-300" :class="{ 'rotate-45': activeFaq === index }">+</span>
              </button>
              <div v-show="activeFaq === index" class="pb-5 -mt-1">
                <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed transition-colors duration-300">{{ faq.answer }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="relative py-28 md:py-40 overflow-hidden">
      <div class="absolute inset-0 bg-gray-950">
        <img src="/website_bg.webp" alt="" class="absolute inset-0 w-full h-full object-cover opacity-60" />
      </div>
      <div class="container mx-auto px-4 text-center relative z-10">
        <div class="max-w-5xl mx-auto">
          <div class="inline-flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/[0.08] px-4 py-1.5 text-[13px] font-medium text-gray-300 mb-6">
            {{ $t('websiteSolution.specialOffer') }}
          </div>
          <h2 class="text-4xl md:text-5xl font-extrabold text-white mb-4">
            {{ $t('websiteTicino.cta.title') }}
          </h2>
          <p class="text-xl md:text-2xl mb-8 text-white/90 font-semibold max-w-3xl mx-auto">
            {{ $t('websiteTicino.cta.description') }}
          </p>
          <router-link to="/contact" class="inline-flex items-center h-14 px-8 bg-white text-gray-900 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/10">
            <Globe class="mr-2 h-5 w-5" />
            {{ $t('websiteTicino.cta.button') }}
          </router-link>

          <div class="flex flex-wrap justify-center gap-6 text-sm text-white/90 mt-8">
            <div class="flex items-center"><Check class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />{{ $t('websiteSolution.trust.freeChatbot') }}</div>
            <div class="flex items-center"><Check class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />{{ $t('websiteSolution.trust.seo') }}</div>
            <div class="flex items-center"><Check class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />{{ $t('websiteSolution.trust.support') }}</div>
          </div>
        </div>
      </div>
    </section>

    <FloatingCta />
  </div>
</template>

<script setup>
import { Globe, Monitor, Users, Zap, Search, Shield, TrendingUp, Check } from 'lucide-vue-next'
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from '../composables/useTheme'
import PartnersSection from '../components/PartnersSection.vue'
import FloatingCta from '../components/FloatingCta.vue'

const { tm, t } = useI18n()
const { isDark } = useTheme()
const activeFaq = ref(null)

const faqQuestions = computed(() => {
  const questions = tm('websiteTicino.faq.questions')
  return Array.isArray(questions) ? questions : []
})

const benefitsList = [
  {
    icon: Globe,
    title: t('websiteTicino.benefits.multilingual.title'),
    description: t('websiteTicino.benefits.multilingual.description')
  },
  {
    icon: Zap,
    title: t('websiteTicino.benefits.performance.title'),
    description: t('websiteTicino.benefits.performance.description')
  },
  {
    icon: Search,
    title: t('websiteTicino.benefits.seo.title'),
    description: t('websiteTicino.benefits.seo.description')
  },
  {
    icon: Shield,
    title: t('websiteTicino.benefits.security.title'),
    description: t('websiteTicino.benefits.security.description')
  },
  {
    icon: Users,
    title: t('websiteTicino.benefits.support.title'),
    description: t('websiteTicino.benefits.support.description')
  },
  {
    icon: TrendingUp,
    title: t('websiteTicino.benefits.growth.title'),
    description: t('websiteTicino.benefits.growth.description')
  }
]

const processSteps = [
  {
    title: t('websiteTicino.process.analysis.title'),
    description: t('websiteTicino.process.analysis.description')
  },
  {
    title: t('websiteTicino.process.design.title'),
    description: t('websiteTicino.process.design.description')
  },
  {
    title: t('websiteTicino.process.development.title'),
    description: t('websiteTicino.process.development.description')
  },
  {
    title: t('websiteTicino.process.deployment.title'),
    description: t('websiteTicino.process.deployment.description')
  }
]

const useCases = computed(() => [
  {
    key: 'tourism',
    title: t('websiteTicino.useCases.tourism.title'),
    description: t('websiteTicino.useCases.tourism.description'),
    features: [
      t('websiteTicino.useCases.tourism.features.booking'),
      t('websiteTicino.useCases.tourism.features.info'),
      t('websiteTicino.useCases.tourism.features.gallery')
    ],
    testimonialText: t('websiteTicino.useCases.tourism.testimonial.text'),
    testimonialAuthor: t('websiteTicino.useCases.tourism.testimonial.author')
  },
  {
    key: 'restaurants',
    title: t('websiteTicino.useCases.restaurants.title'),
    description: t('websiteTicino.useCases.restaurants.description'),
    features: [
      t('websiteTicino.useCases.restaurants.features.menu'),
      t('websiteTicino.useCases.restaurants.features.reservations'),
      t('websiteTicino.useCases.restaurants.features.gallery')
    ],
    testimonialText: t('websiteTicino.useCases.restaurants.testimonial.text'),
    testimonialAuthor: t('websiteTicino.useCases.restaurants.testimonial.author')
  },
  {
    key: 'medical',
    title: t('websiteTicino.useCases.medical.title'),
    description: t('websiteTicino.useCases.medical.description'),
    features: [
      t('websiteTicino.useCases.medical.features.appointments'),
      t('websiteTicino.useCases.medical.features.info'),
      t('websiteTicino.useCases.medical.features.services')
    ],
    testimonialText: t('websiteTicino.useCases.medical.testimonial.text'),
    testimonialAuthor: t('websiteTicino.useCases.medical.testimonial.author')
  },
  {
    key: 'ecommerce',
    title: t('websiteTicino.useCases.ecommerce.title'),
    description: t('websiteTicino.useCases.ecommerce.description'),
    features: [
      t('websiteTicino.useCases.ecommerce.features.catalog'),
      t('websiteTicino.useCases.ecommerce.features.payments'),
      t('websiteTicino.useCases.ecommerce.features.tracking')
    ],
    testimonialText: t('websiteTicino.useCases.ecommerce.testimonial.text'),
    testimonialAuthor: t('websiteTicino.useCases.ecommerce.testimonial.author')
  }
])

const toggleFaq = (index) => {
  activeFaq.value = activeFaq.value === index ? null : index
}

defineOptions({
  name: 'WebsiteTicinoPage'
})
</script>
